/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Safely parses a JSON string, returning undefined if parsing fails
 */
export function safeJSONParse<T>(str: string): T | undefined {
  try {
    return JSON.parse(str) as T;
  } catch (e) {
    console.error('Failed to parse JSON:', e);
    return undefined;
  }
}

/**
 * Parse URL hash parameters into an object
 */
export function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {};
  
  // Remove the leading # if present
  const hashString = hash.startsWith('#') ? hash.substring(1) : hash;
  
  // Split on & and process each key-value pair
  hashString.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    if (key && value) {
      params[decodeURIComponent(key)] = decodeURIComponent(value);
    }
  });
  
  return params;
}